import React, { useState, useEffect, useRef } from "react";
import Button from "../../components/common/Button";
import FileUpload from "../../components/forms/FileUpload";
import ListingSidebar from "../../components/listings/ListingSidebar";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import toast from "react-hot-toast";
import {
  ROOM_TYPE_OPTIONS,
  FACILITY_OPTIONS,
  NEPAL_PROVINCES,
} from "../../constants/listingOptions";
import { getImageUrl } from "../../utils/imageUrl";

export default function AddListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prevent double submission even if the user clicks very quickly
  const submittingRef = useRef(false);
  const [acceptTerms, setAcceptTerms] = useState(isEditMode);

  const [form, setForm] = useState({
    title: "",
    description: "",
    roomType: "",
    rentAmount: "",
    availableFrom: "",
    leaseDuration: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    neighborhood: "",
    rooms: 1,
    kitchen: false,
    bathroom: true,
    securityDeposit: "",
    utilitiesIncluded: "",
    houseRules: "",
    additionalInfo: "",
  });

  const [selectedFacilities, setSelectedFacilities] = useState([]);
  // now holds File[] instead of URL strings
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  const [existingImages, setExistingImages] = useState([]);

  const [video, setVideo] = useState(null);
  const [existingVideo, setExistingVideo] = useState("");
  const [videoError, setVideoError] = useState("");

  function change(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }
  // Video validator
  function validateVideoDuration(file) {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject("Please select a video.");
        return;
      }

      // Maximum 100 MB
      const MAX_SIZE = 100 * 1024 * 1024;

      if (file.size > MAX_SIZE) {
        reject("Video size must not exceed 100 MB.");
        return;
      }

      // Accept only video files
      if (!file.type.startsWith("video/")) {
        reject("Only video files are allowed.");
        return;
      }

      const videoEl = document.createElement("video");
      videoEl.preload = "metadata";

      videoEl.onloadedmetadata = () => {
        URL.revokeObjectURL(videoEl.src);

        const duration = videoEl.duration;

        if (duration < 30 || duration > 35) {
          reject("Video duration must be between 30 and 35 seconds.");
          return;
        }

        resolve();
      };

      videoEl.onerror = () => {
        URL.revokeObjectURL(videoEl.src);
        reject("Unable to read the selected video.");
      };

      videoEl.src = URL.createObjectURL(file);
    });
  }
  // Handle video selection
  async function handleVideoChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    setVideoError("");

    try {
      await validateVideoDuration(file);
      setVideo(file);
    } catch (err) {
      setVideo(null);
      setVideoError(err);
      e.target.value = ""; // reset input
    }
  }

  function toggleFacility(facility) {
    setSelectedFacilities((prev) =>
      prev.includes(facility)
        ? prev.filter((f) => f !== facility)
        : [...prev, facility],
    );
  }

  /**
   * HANDLE IMAGES
   * -------------
   * We no longer upload to /upload separately.
   * We just store the File objects and send them
   * along with the form as multipart/form-data to /listings.
   */

  // function handleImages(input) {
  //   let files = [];

  //   if (input?.target?.files) {
  //     files = Array.from(input.target.files);
  //   } else if (Array.isArray(input)) {
  //     files = input;
  //   }

  //   if (!files.length) return;

  //   // Maximum images allowed
  //   if (files.length > 10) {
  //     toast.error("Maximum 10 images are allowed.");
  //     return;
  //   }

  //   // Maximum image size (5 MB each)
  //   const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  //   const invalidImage = files.find(
  //     (file) => !file.type.startsWith("image/") || file.size > MAX_IMAGE_SIZE,
  //   );

  //   if (invalidImage) {
  //     toast.error("Each image must be an image file and smaller than 5 MB.");
  //     return;
  //   }

  //   setImages(files);
  // }

  function handleImages(input) {
    let files = [];

    if (input?.target?.files) {
      files = Array.from(input.target.files);
    } else if (Array.isArray(input)) {
      files = input;
    }

    if (!files.length) return;

    const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

    const invalidImage = files.find(
      (file) => !file.type.startsWith("image/") || file.size > MAX_IMAGE_SIZE,
    );

    if (invalidImage) {
      toast.error("Each image must be an image file and smaller than 5 MB.");
      return;
    }

    // Existing new images + newly selected images
    const combinedImages = [...images, ...files];

    // Remove duplicates
    const uniqueImages = combinedImages.filter(
      (file, index, array) =>
        index ===
        array.findIndex(
          (item) =>
            item.name === file.name &&
            item.size === file.size &&
            item.lastModified === file.lastModified,
        ),
    );

    if (uniqueImages.length > 10) {
      toast.error("Maximum 10 new images are allowed.");
      return;
    }

    setImages(uniqueImages);

    // Generate previews
    const previews = uniqueImages.map((file) => URL.createObjectURL(file));

    setImagePreviews((oldPreviews) => {
      oldPreviews.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      });

      return previews;
    });
  }

  useEffect(() => {
    if (!isEditMode) return;

    let isMounted = true;

    async function loadListing() {
      try {
        setLoading(true);

        const { data } = await api.get(`/listings/${id}`);

        if (!isMounted) return;

        const location = (data.location || "").split(",").map((x) => x.trim());

        setForm({
          title: data.title || "",
          description: data.description || "",
          roomType: data.roomType || "",
          rentAmount: data.rentAmount || "",
          availableFrom: data.availableFrom
            ? data.availableFrom.substring(0, 10)
            : "",
          leaseDuration: data.leaseDuration || "",

          streetAddress: location[0] || "",
          city: location[1] || "",
          state: location[2] || "",
          zipCode: location[3] || "",

          neighborhood: data.neighborhood || "",

          rooms: data.rooms || 1,

          kitchen: Boolean(data.kitchen),
          bathroom: Boolean(data.bathroom),

          securityDeposit: data.securityDeposit || "",

          utilitiesIncluded: data.utilitiesIncluded || "",

          houseRules: data.houseRules || "",

          additionalInfo: data.additionalInfo || "",
        });

        setSelectedFacilities(data.facilities || []);
        setExistingImages(data.images || []);
        setExistingVideo(data.video || "");
        setAcceptTerms(true);
      } catch (err) {
        console.error(err);

        toast.error(err?.response?.data?.message || "Unable to load listing.");

        navigate("/admin", {
          replace: true,
        });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadListing();

    return () => {
      isMounted = false;
    };
  }, [id, isEditMode, navigate]);

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  // RESET FORM AFTER SUCCESSFUL SUBMIT
  // function resetForm() {
  //   imagePreviews.forEach((url) => URL.revokeObjectURL(url));

  //   setForm({
  //     title: "",
  //     description: "",
  //     roomType: "",
  //     rentAmount: "",
  //     availableFrom: "",
  //     leaseDuration: "",
  //     streetAddress: "",
  //     city: "",
  //     state: "",
  //     zipCode: "",
  //     neighborhood: "",
  //     rooms: 1,
  //     kitchen: false,
  //     bathroom: true,
  //     securityDeposit: "",
  //     utilitiesIncluded: "",
  //     houseRules: "",
  //     additionalInfo: "",
  //   });

  //   setSelectedFacilities([]);

  //   // Clear uploaded images
  //   setImages([]);
  //   setImagePreviews([]);

  //   // Clear uploaded video
  //   setVideo(null);

  //   // Clear validation
  //   setAcceptTerms(false);
  //   setVideoError("");
  // }

  function resetForm() {
    // Revoke preview URLs
    imagePreviews.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        console.warn("Unable to revoke preview URL:", error);
      }
    });

    setForm({
      title: "",
      description: "",
      roomType: "",
      rentAmount: "",
      availableFrom: "",
      leaseDuration: "",
      streetAddress: "",
      city: "",
      state: "",
      zipCode: "",
      neighborhood: "",
      rooms: 1,
      kitchen: false,
      bathroom: true,
      securityDeposit: "",
      utilitiesIncluded: "",
      houseRules: "",
      additionalInfo: "",
    });

    // Facilities
    setSelectedFacilities([]);

    // New images
    setImages([]);

    // Image previews
    setImagePreviews([]);

    // Existing images
    setExistingImages([]);

    // New video
    setVideo(null);

    // Existing video
    setExistingVideo("");

    // Video validation error
    setVideoError("");

    // Terms
    setAcceptTerms(false);
  }

  function buildLocation() {
    return [
      form.streetAddress.trim(),
      form.city.trim(),
      form.state.trim(),
      form.zipCode.trim(),
    ]
      .filter(Boolean)
      .join(", ");
  }

  // async function submit(e) {
  //   e.preventDefault();
  //   if (loading) return;

  //   if (!acceptTerms) {
  //     toast.error("Please accept the Terms & Conditions.");
  //     return;
  //   }

  //   if (selectedFacilities.length === 0) {
  //     toast.error("Please select at least one facility.");
  //     return;
  //   }

  //   if (images.length === 0 && existingImages.length === 0) {
  //     toast.error("Please upload at least one room image.");
  //     return;
  //   }

  //   if (Number(form.rentAmount) <= 0) {
  //     toast.error("Rent must be greater than zero.");
  //     return;
  //   }

  //   setLoading(true);

  //   if (!form.title || !form.roomType || !form.rentAmount) {
  //     toast.error("Please fill at least title, room type and rent.");
  //     return;
  //   }

  //   if (selectedFacilities.length === 0) {
  //     toast.error("Select at least one facility.");
  //     return;
  //   }

  //   if (images.length === 0 && existingImages.length === 0) {
  //     toast.error("Please upload at least one image.");
  //     return;
  //   }

  //   if (!acceptTerms) {
  //     toast.error("Please accept the Terms & Conditions.");
  //     return;
  //   }

  //   if (Number(form.rentAmount) <= 0) {
  //     toast.error("Rent must be greater than zero.");
  //     return;
  //   }

  //   try {
  //     const fd = new FormData();

  //     // basic fields
  //     fd.append("title", form.title);
  //     fd.append("roomType", form.roomType);
  //     fd.append("location", buildLocation());
  //     fd.append("rentAmount", form.rentAmount);

  //     // facilities: backend createListing can split comma-separated string
  //     fd.append("facilities", selectedFacilities.join(","));

  //     fd.append("rooms", form.rooms);
  //     fd.append("kitchen", form.kitchen); // will arrive as "true"/"false"
  //     fd.append("bathroom", form.bathroom); // same

  //     // extra fields
  //     fd.append("description", form.description);
  //     fd.append("availableFrom", form.availableFrom);
  //     fd.append("leaseDuration", form.leaseDuration);
  //     fd.append("securityDeposit", form.securityDeposit);
  //     fd.append("utilitiesIncluded", form.utilitiesIncluded);
  //     fd.append("houseRules", form.houseRules);
  //     fd.append("additionalInfo", form.additionalInfo);
  //     fd.append("neighborhood", form.neighborhood);
  //     fd.append("acceptTerms", acceptTerms);

  //     // append files under field name 'images'
  //     images.forEach((file) => {
  //       fd.append("images", file);
  //     });

  //     if (video) fd.append("video", video);
  //     for (const [key, value] of fd.entries()) {
  //       console.log(key, value);
  //     }

  //     let res;

  //     if (isEditMode) {
  //       res = await api.put(`/listings/${id}`, fd, {
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //         },
  //       });

  //       toast.success("Listing updated successfully.");
  //       navigate("/admin");
  //     } else {
  //       res = await api.post("/listings", fd, {
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //         },
  //       });

  //       toast.success("Listing created and sent for approval.");
  //       navigate("/owner", {
  //         replace: true,
  //       });
  //     }

  //     // toast.success("Listing created and sent for approval");
  //     if (!isEditMode) {
  //       resetForm();
  //     }
  //   } catch (err) {
  //     console.error(err);

  //     toast.error(
  //       err?.response?.data?.message ||
  //         err.message ||
  //         "Unable to save listing.",
  //     );
  //   } finally {
  //     setLoading(false);
  //   }
  // }

  async function submit(e) {
    e.preventDefault();

    // ============================================================
    // HARD LOCK
    // Prevent double-click / multiple submit events
    // ============================================================

    if (submittingRef.current) {
      return;
    }

    // ============================================================
    // VALIDATION
    // Do validation BEFORE setting submitting = true
    // ============================================================

    if (!acceptTerms) {
      toast.error("Please accept the Terms & Conditions.");
      return;
    }

    if (!form.title.trim()) {
      toast.error("Please enter a room title.");
      return;
    }

    if (!form.roomType) {
      toast.error("Please select a room type.");
      return;
    }

    if (!form.rentAmount || Number(form.rentAmount) <= 0) {
      toast.error("Rent must be greater than zero.");
      return;
    }

    if (!form.streetAddress.trim()) {
      toast.error("Please enter the street address.");
      return;
    }

    if (!form.city.trim()) {
      toast.error("Please enter the city.");
      return;
    }

    if (!form.state) {
      toast.error("Please select a province.");
      return;
    }

    if (selectedFacilities.length === 0) {
      toast.error("Please select at least one facility.");
      return;
    }

    if (images.length === 0 && existingImages.length === 0) {
      toast.error("Please upload at least one room image.");
      return;
    }

    // ============================================================
    // LOCK SUBMISSION IMMEDIATELY
    // useRef prevents rapid double-clicks
    // ============================================================

    submittingRef.current = true;
    setSubmitting(true);

    try {
      const fd = new FormData();

      // ==========================================================
      // BASIC INFORMATION
      // ==========================================================

      fd.append("title", form.title.trim());
      fd.append("roomType", form.roomType);
      fd.append("location", buildLocation());
      fd.append("rentAmount", form.rentAmount);

      // ==========================================================
      // FACILITIES
      // ==========================================================

      fd.append("facilities", selectedFacilities.join(","));

      fd.append("rooms", String(form.rooms));
      fd.append("kitchen", String(form.kitchen));
      fd.append("bathroom", String(form.bathroom));

      // ==========================================================
      // ADDITIONAL FIELDS
      // ==========================================================

      fd.append("description", form.description.trim());

      fd.append("availableFrom", form.availableFrom || "");

      fd.append("leaseDuration", form.leaseDuration || "");

      fd.append("securityDeposit", form.securityDeposit || "");

      fd.append("utilitiesIncluded", form.utilitiesIncluded || "");

      fd.append("houseRules", form.houseRules.trim());

      fd.append("additionalInfo", form.additionalInfo.trim());

      fd.append("neighborhood", form.neighborhood.trim());

      fd.append("acceptTerms", String(acceptTerms));

      // ==========================================================
      // NEW IMAGES
      // ==========================================================

      images.forEach((file) => {
        fd.append("images", file);
      });

      // ==========================================================
      // VIDEO
      // ==========================================================

      if (video) {
        fd.append("video", video);
      }

      // ==========================================================
      // DEBUG - REMOVE IN PRODUCTION
      // ==========================================================

      console.log("Submitting listing:", isEditMode ? `EDIT ${id}` : "CREATE");

      // ==========================================================
      // CREATE / UPDATE
      // ==========================================================

      let res;

      if (isEditMode) {
        // --------------------------------------------------------
        // UPDATE LISTING
        // --------------------------------------------------------

        res = await api.put(`/listings/${id}`, fd, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success("Listing updated successfully.");

        // Reset after successful update
        resetForm();

        // Go back to admin
        navigate("/admin", {
          replace: true,
        });
      } else {
        // --------------------------------------------------------
        // CREATE LISTING
        // --------------------------------------------------------

        res = await api.post("/listings", fd, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success("Listing created and sent for approval.");

        // ========================================================
        // RESET FORM AFTER SUCCESSFUL CREATION
        // ========================================================

        resetForm();

        // Navigate after reset
        navigate("/owner", {
          replace: true,
        });
      }

      console.log("Listing saved successfully:", res?.data);
    } catch (err) {
      console.error("Error saving listing:", err);

      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save listing.",
      );
    } finally {
      // ==========================================================
      // UNLOCK ONLY AFTER REQUEST FINISHES
      // ==========================================================

      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg font-semibold">Loading listing...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f8fa] pb-20">
      {/* ================= HEADER ================= */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-8 py-2 flex justify-between items-center">
          <div>
            {/* <h1 className="text-5xl font-bold text-gray-900">List Your Room</h1> */}
            <h1 className="text-5xl font-bold text-gray-900">
              {isEditMode ? "Edit Listing" : "List Your Room"}
            </h1>

            <p className="text-gray-500 mt-3 text-lg">
              {/* Fill in the details below to list your rental room. */}
              {isEditMode
                ? "Update the information below."
                : "Fill in the details below to list your rental room."}
            </p>
          </div>

          {/* Right Illustration */}
          <img src="/room.jpeg" alt="House" className="w-72 hidden lg:block" />
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-8 py-4">
        <div className="grid grid-cols-12 gap-8">
          {/* LEFT SIDEBAR */}
          {/* <div className="col-span-3"> */}
          <div className="col-span-3 self-start sticky top-8">
            <ListingSidebar currentStep={1} />
          </div>

          <div className="col-span-9">
            <form onSubmit={submit} className="space-y-8" noValidate>
              {/* ========================================= */}
              {/* ROOM DETAILS */}
              {/* ========================================= */}
              <fieldset disabled={loading || submitting}>
                <section
                  id="basic-details"
                  className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="px-8 py-6 border-b bg-gray-50 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      🏠
                    </div>

                    <div>
                      <h2 className="font-bold text-xl">1. Room Details</h2>

                      <p className="text-gray-500 text-sm">
                        Basic information about your room
                      </p>
                    </div>
                  </div>

                  <div className="p-8 space-y-8">
                    {/* Row 1 */}

                    <div className="grid lg:grid-cols-3 gap-6">
                      {/* TITLE */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Room Title
                        </label>

                        <input
                          name="title"
                          required
                          value={form.title}
                          autoComplete="off"
                          onChange={change}
                          placeholder="e.g. Cozy Room Near Bhathbhateni"
                          className="w-full h-12 rounded-xl border border-gray-300 px-4 focus:ring-2 focus:ring-orange-400 outline-none"
                        />
                      </div>

                      {/* ROOM TYPE */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Room Type
                        </label>

                        <select
                          name="roomType"
                          required
                          value={form.roomType}
                          onChange={change}
                          className="w-full h-12 rounded-xl border border-gray-300 px-4 bg-white focus:ring-2 focus:ring-orange-400 outline-none"
                        >
                          <option value="">Select room type</option>

                          {ROOM_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* RENT */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Monthly Rent (NPR)
                        </label>

                        <input
                          type="number"
                          name="rentAmount"
                          required
                          min={1}
                          value={form.rentAmount}
                          onChange={change}
                          placeholder="e.g. 8000"
                          className="w-full h-12 rounded-xl border border-gray-300 px-4 focus:ring-2 focus:ring-orange-400 outline-none"
                        />
                      </div>
                    </div>

                    {/* DESCRIPTION */}

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Description
                      </label>

                      <textarea
                        rows={5}
                        name="description"
                        value={form.description}
                        onChange={change}
                        placeholder="Describe your room, facilities and surroundings..."
                        className="w-full rounded-xl border border-gray-300 p-4 resize-none focus:ring-2 focus:ring-orange-400 outline-none"
                      />
                    </div>

                    {/* ROW 2 */}

                    <div className="grid lg:grid-cols-3 gap-6">
                      {/* ROOMS */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Number of Rooms
                        </label>

                        <input
                          type="number"
                          min={1}
                          name="rooms"
                          value={form.rooms}
                          onChange={change}
                          className="w-full h-12 rounded-xl border border-gray-300 px-4"
                        />
                      </div>

                      {/* AVAILABLE */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Available From
                        </label>

                        <input
                          type="date"
                          name="availableFrom"
                          value={form.availableFrom}
                          onChange={change}
                          className="w-full h-12 rounded-xl border border-gray-300 px-4"
                        />
                      </div>

                      {/* LEASE */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Lease Duration
                        </label>

                        <select
                          name="leaseDuration"
                          value={form.leaseDuration}
                          onChange={change}
                          className="w-full h-12 rounded-xl border border-gray-300 px-4 bg-white"
                        >
                          <option value="">Select duration</option>

                          <option value="Flexible">Flexible</option>

                          <option value="3 Months">3 Months</option>

                          <option value="6 Months">6 Months</option>

                          <option value="12 Months">12 Months</option>

                          <option value="Long Term">Long Term</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </section>

                {/* ========================================= */}
                {/* 2. LOCATION */}
                {/* ========================================= */}

                <section
                  id="location"
                  className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="px-8 py-6 border-b bg-gray-50 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-xl">
                      📍
                    </div>

                    <div>
                      <h2 className="font-bold text-xl">2. Location</h2>

                      <p className="text-gray-500 text-sm">
                        Help tenants find your room easily.
                      </p>
                    </div>
                  </div>

                  <div className="p-8 space-y-6">
                    {/* STREET */}

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Street Address
                      </label>

                      <input
                        name="streetAddress"
                        required
                        autoComplete="street-address"
                        value={form.streetAddress}
                        onChange={change}
                        placeholder="e.g. 123 Main Street"
                        className="w-full h-12 rounded-xl border border-gray-300 px-4 focus:ring-2 focus:ring-orange-400 outline-none"
                      />
                    </div>

                    {/* ROW */}

                    <div className="grid lg:grid-cols-3 gap-6">
                      {/* CITY */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          City
                        </label>

                        <input
                          name="city"
                          required
                          autoComplete="address-level2"
                          value={form.city}
                          onChange={change}
                          placeholder="Kathmandu"
                          className="w-full h-12 rounded-xl border border-gray-300 px-4"
                        />
                      </div>

                      {/* PROVINCE */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Province
                        </label>

                        <select
                          name="state"
                          required
                          value={form.state}
                          onChange={change}
                          className="w-full h-12 rounded-xl border border-gray-300 px-4 bg-white"
                        >
                          <option value="">Select Province</option>

                          {NEPAL_PROVINCES.map((province) => (
                            <option key={province} value={province}>
                              {province}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* ZIP */}

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          ZIP Code
                        </label>

                        <input
                          name="zipCode"
                          autoComplete="postal-code"
                          value={form.zipCode}
                          onChange={change}
                          placeholder="44600"
                          className="w-full h-12 rounded-xl border border-gray-300 px-4"
                        />
                      </div>
                    </div>

                    {/* NEIGHBORHOOD */}

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Area / Neighborhood
                      </label>

                      <textarea
                        rows={3}
                        name="neighborhood"
                        value={form.neighborhood}
                        onChange={change}
                        placeholder="Describe nearby schools, hospitals, bus park, shopping malls..."
                        className="w-full rounded-xl border border-gray-300 p-4 resize-none focus:ring-2 focus:ring-orange-400 outline-none"
                      />
                    </div>
                  </div>
                </section>

                {/* ========================================= */}
                {/* 3. FACILITIES */}
                {/* ========================================= */}

                <section
                  id="amenities"
                  className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="px-8 py-6 border-b bg-gray-50 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-xl">
                      ⭐
                    </div>

                    <div>
                      <h2 className="font-bold text-xl">
                        3. Facilities & Amenities
                      </h2>

                      <p className="text-gray-500 text-sm">
                        Select all that apply
                      </p>
                    </div>
                  </div>

                  <div className="p-8">
                    {/* Facility Grid */}

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {FACILITY_OPTIONS.map((facility) => {
                        const active = selectedFacilities.includes(facility);

                        return (
                          <button
                            key={facility}
                            type="button"
                            onClick={() => toggleFacility(facility)}
                            className={`border rounded-2xl h-14 transition-all flex items-center justify-center font-medium

                      ${
                        active
                          ? "bg-orange-500 border-orange-500 text-white shadow-md"
                          : "bg-white hover:border-orange-400 hover:bg-orange-50 border-gray-300 text-gray-700"
                      }`}
                          >
                            {facility}
                          </button>
                        );
                      })}
                    </div>

                    {/* Kitchen Bathroom */}

                    <div className="grid md:grid-cols-2 gap-6 mt-8">
                      <label className="flex items-center gap-3 p-4 border rounded-xl hover:border-orange-400 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.kitchen}
                          name="kitchen"
                          onChange={change}
                          className="w-5 h-5 accent-orange-500"
                        />

                        <div>
                          <p className="font-semibold">Kitchen Access</p>

                          <p className="text-sm text-gray-500">
                            Shared or Private Kitchen
                          </p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-4 border rounded-xl hover:border-orange-400 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.bathroom}
                          name="bathroom"
                          onChange={change}
                          className="w-5 h-5 accent-orange-500"
                        />

                        <div>
                          <p className="font-semibold">Private Bathroom</p>

                          <p className="text-sm text-gray-500">
                            Bathroom attached with room
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </section>

                {/* ========================================= */}
                {/* 4. PHOTOS & VIDEO */}
                {/* ========================================= */}

                <section
                  id="photos"
                  className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="px-8 py-6 border-b bg-gray-50 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-xl">
                      📷
                    </div>

                    <div>
                      <h2 className="font-bold text-xl">4. Photos & Video</h2>

                      <p className="text-gray-500 text-sm">
                        Upload high quality photos and one short video.
                      </p>
                    </div>
                  </div>

                  <div className="p-8">
                    {/* IMAGE */}
                    <label className="block text-sm font-semibold mb-3">
                      Room Photos (Maximum 10)
                    </label>

                    {/* Existing Images (Edit Mode Only) */}
                    {isEditMode && existingImages.length > 0 && (
                      <>
                        <p className="text-sm font-medium text-gray-700 mb-3">
                          Existing Photos
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                          {existingImages.map((img, index) => (
                            <div
                              key={index}
                              className="relative rounded-xl overflow-hidden"
                            >
                              <img
                                src={getImageUrl(img)}
                                alt={`Room ${index + 1}`}
                                className="h-28 w-full object-cover border rounded-xl"
                              />

                              <button
                                type="button"
                                onClick={() =>
                                  setExistingImages((prev) =>
                                    prev.filter((_, i) => i !== index),
                                  )
                                }
                                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white text-sm hover:bg-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Upload New Images */}
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center hover:border-orange-400 transition">
                      <div className="text-5xl mb-4">📷</div>

                      <p className="font-semibold">Upload New Photos</p>

                      <p className="text-gray-500 text-sm mt-2">
                        JPG, PNG • Maximum 5MB each
                      </p>

                      <div className="mt-6">
                        <FileUpload
                          onChange={handleImages}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {images.length > 0 && (
                      <div className="mt-8">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                          New Photos Preview
                        </h4>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                          {imagePreviews.map((preview, index) => (
                            <div
                              key={index}
                              className="relative rounded-xl overflow-hidden border"
                            >
                              <img
                                src={preview}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-28 object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* VIDEO */}
                    <div className="mt-8">
                      <label className="block text-sm font-semibold mb-3">
                        Room Video (Optional)
                      </label>

                      <div className="flex items-center gap-5">
                        <input
                          type="file"
                          disabled={loading}
                          accept="video/*"
                          onChange={handleVideoChange}
                          className="block border rounded-xl px-3 py-2"
                        />

                        <span className="text-gray-500 text-sm">
                          Max 30–35 seconds
                        </span>
                      </div>

                      {/* Existing video (Edit Mode) */}
                      {existingVideo && !video && (
                        <div className="mt-4">
                          <div className="flex justify-between items-center mb-3">
                            <p className="text-sm font-semibold">
                              Current Video
                            </p>

                            <button
                              type="button"
                              onClick={() => setExistingVideo("")}
                              className="text-red-600 text-sm hover:underline"
                            >
                              Remove Video
                            </button>
                          </div>

                          <video
                            controls
                            className="w-full h-[80vh] rounded-xl object-contain bg-black"
                            src={getImageUrl(existingVideo)}
                          />
                        </div>
                      )}

                      {/* Newly selected video */}
                      {video && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-green-600 mb-2">
                            New Video Selected
                          </p>

                          <video
                            controls
                            className="w-full h-[80vh] rounded-xl object-contain bg-black"
                            src={URL.createObjectURL(video)}
                          />

                          <p className="mt-2 text-sm text-green-600">
                            ✔ {video.name}
                          </p>
                        </div>
                      )}

                      {videoError && (
                        <p className="mt-2 text-red-600 text-sm">
                          {videoError}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                {/* ========================================= */}
                {/* 5. ADDITIONAL INFORMATION */}
                {/* ========================================= */}

                <section
                  id="additional"
                  className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="px-8 py-6 border-b bg-gray-50 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-xl">
                      ℹ️
                    </div>

                    <div>
                      <h2 className="font-bold text-xl">
                        5. Additional Information
                      </h2>

                      <p className="text-gray-500 text-sm">
                        Optional details for tenants
                      </p>
                    </div>
                  </div>

                  <div className="p-8">
                    {/* FIRST ROW */}

                    <div className="grid lg:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Security Deposit (NPR)
                        </label>

                        <input
                          type="number"
                          name="securityDeposit"
                          value={form.securityDeposit}
                          onChange={change}
                          placeholder="12000"
                          className="w-full h-12 border rounded-xl px-4"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Utilities Included
                        </label>

                        <select
                          name="utilitiesIncluded"
                          value={form.utilitiesIncluded}
                          onChange={change}
                          className="w-full h-12 border rounded-xl px-4 bg-white"
                        >
                          <option value="">Select option</option>

                          <option value="None">None</option>

                          <option value="Some Utilities">Some Utilities</option>

                          <option value="All Utilities Included">
                            All Utilities Included
                          </option>
                        </select>
                      </div>
                    </div>

                    {/* SECOND ROW */}

                    <div className="grid lg:grid-cols-2 gap-6 mt-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          House Rules
                        </label>

                        <textarea
                          rows={4}
                          name="houseRules"
                          value={form.houseRules}
                          onChange={change}
                          placeholder="No pets, no parties, visitors not allowed..."
                          className="w-full rounded-xl border p-4 resize-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Additional Information
                        </label>

                        <textarea
                          rows={4}
                          name="additionalInfo"
                          value={form.additionalInfo}
                          onChange={change}
                          placeholder="Anything else tenants should know..."
                          className="w-full rounded-xl border p-4 resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* ========================================= */}
                {/* FOOTER */}
                {/* ========================================= */}

                <div className="border-t border-gray-200 pt-8 mt-10">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                    {/* Left Side */}
                    <div className="flex items-start gap-3">
                      <input
                        id="terms"
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="mt-1 h-5 w-5 rounded accent-orange-500 cursor-pointer flex-shrink-0"
                      />

                      <label
                        htmlFor="terms"
                        className="text-sm text-gray-600 leading-6"
                      >
                        I confirm that all the information provided is accurate
                        and I agree to the{" "}
                        <button
                          type="button"
                          onClick={() => navigate("/terms")}
                          className="text-orange-500 font-semibold hover:underline"
                        >
                          Terms & Conditions
                        </button>{" "}
                        and{" "}
                        <button
                          type="button"
                          onClick={() => navigate("/privacy-policy")}
                          className="text-orange-500 font-semibold hover:underline"
                        >
                          Privacy Policy
                        </button>
                        .
                      </label>
                    </div>

                    {/* Right Side */}
                    <div className="flex justify-start lg:justify-end gap-3">
                      <Button
                        type="button"
                        disabled={loading || submitting}
                        variant="outline"
                        className="px-8"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </Button>
                      
                      <Button
                        type="submit"
                        variant="primary"
                        className="px-10"
                        disabled={loading || submitting || !acceptTerms}
                      >
                        {submitting
                          ? isEditMode
                            ? "Saving..."
                            : "Publishing..."
                          : isEditMode
                            ? "Save Changes"
                            : "Publish Listing"}
                      </Button>
                    </div>
                  </div>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
